// src/features/admin/components/settings/integrations/components/CalendarSyncTab.tsx
import React from 'react';
import {
  CalendarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const CALENDAR_PROVIDERS = [
  {
    name: 'Google Calendar',
    description: 'Bi-directional sync with Google Workspace calendars.',
    protocol: 'CalDAV / Google API',
    features: ['Two-way sync', 'Event creation', 'Invitations', 'Reminders']
  },
  {
    name: 'Microsoft Outlook 365',
    description: 'Integration with Microsoft 365 calendars via Graph API.',
    protocol: 'Microsoft Graph API',
    features: ['Two-way sync', 'Meeting rooms', 'Teams integration', 'Categories']
  },
  {
    name: 'Apple Calendar',
    description: 'CalDAV-based sync with Apple Calendar / iCloud.',
    protocol: 'CalDAV',
    features: ['Two-way sync', 'iCloud sync', 'Shared calendars', 'Reminders']
  },
  {
    name: 'Generic CalDAV',
    description: 'Connect to any CalDAV-compatible calendar server.',
    protocol: 'CalDAV',
    features: ['Standard protocol', 'Self-hosted support', 'Nextcloud', 'Radicale']
  }
];

const CalendarSyncTab: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Coming Soon Banner */}
      <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-6 text-center">
        <ClockIcon className="h-12 w-12 text-amber-600 dark:text-amber-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-200 mb-2">
          Calendar Sync - Coming in Phase 2
        </h3>
        <p className="text-sm text-amber-700 dark:text-amber-300 max-w-2xl mx-auto">
          Synchronize EdgeCalendar with external calendar providers for unified scheduling.
          This feature is scheduled for Phase 2 of development (Weeks 11-18).
        </p>
      </div>

      {/* Preview */}
      <div>
        <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <CalendarIcon className="h-5 w-5 mr-2 text-lex-accent" />
          Planned Calendar Integrations
        </h4>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {CALENDAR_PROVIDERS.map((provider, index) => (
            <div
              key={index}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 opacity-75"
            >
              <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                {provider.name}
              </h5>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                {provider.description}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                <strong>Protocol:</strong> {provider.protocol}
              </p>
              <div className="flex flex-wrap gap-1">
                {provider.features.map((feature, idx) => (
                  <span
                    key={idx}
                    className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sync Benefits */}
      <div className="bg-lex-accent-soft dark:bg-lex-accent-soft border border-lex-accent dark:border-lex-accent rounded-lg p-4">
        <h5 className="text-sm font-semibold text-lex-accent dark:text-lex-accent mb-2">
          Calendar Sync Benefits
        </h5>
        <ul className="text-xs text-lex-accent dark:text-lex-accent space-y-1">
          <li>• <strong>Unified View:</strong> See all events in EdgeCalendar</li>
          <li>• <strong>Matter Events:</strong> Automatically link court dates to matters</li>
          <li>• <strong>Deadline Tracking:</strong> Sync deadlines with personal calendar</li>
          <li>• <strong>Team Visibility:</strong> Share availability across calendars</li>
        </ul>
      </div>
    </div>
  );
};

export default CalendarSyncTab;
